import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

export default function ProgramBuyModal({ eventId, eventTitle, price, open, setOpen }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const buy = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Email is required — that's where your program link goes");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/program/${eventId}/buy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Couldn't start checkout — please try again");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="program-buy-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md border border-clay-border bg-clay-surface p-6 sm:p-8"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-1">Digital Race Day Program</p>
                <h3 className="font-display font-extrabold uppercase text-2xl tracking-tight">{eventTitle}</h3>
              </div>
              <button data-testid="program-buy-close" onClick={() => setOpen(false)} className="text-clay-muted hover:text-clay-text p-1" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-clay-muted text-sm leading-relaxed mb-6">
              {price} gets you the full program — event list, heat winners sheet, life members and sponsors — viewable
              and printable anytime from your own link, emailed to you after payment.
            </p>
            <form onSubmit={buy} className="space-y-4">
              <input data-testid="program-buy-name" placeholder="Your name (optional)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
              <input data-testid="program-buy-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} required />
              <button
                data-testid="program-buy-submit"
                disabled={loading}
                className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <BookOpen className="w-5 h-5" />}
                Buy Program — {price}
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-center">
                Secure card payment — program link lands in your inbox
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
